
import React, { useState } from 'react';
import { Farm, DocumentSource, Document, MapFeature, GeometryType, MapFeatureType } from '../types';
import { parseGpxFile, determineGeometryType } from '../services/mapUtils';
import { MockAPI } from '../services/api';
import { Button } from '../components/ui';
import { Upload, X } from 'lucide-react';
import MapFeatureEditor, { getCategoryFromFeature } from '../components/MapFeatureEditor';
import { SyncService } from '../services/syncService';

interface MapImportModalProps {
    farm: Farm;
    onClose: () => void;
    onSuccess: () => void;
}

interface ImportItem {
    id: string;
    selected: boolean;
    feature: Partial<MapFeature>;
    rawGeometry: any;
    rawType: 'Track' | 'Route' | 'Waypoint';
}
const SUPPORTED_MAP_ENTITY_TYPES = new Set(['farm', 'asset', 'project', 'map_feature']);

const MapImportModal: React.FC<MapImportModalProps> = ({ farm, onClose, onSuccess }) => {
    const [step, setStep] = useState<1 | 2>(1);
    const [file, setFile] = useState<File | null>(null);
    const [items, setItems] = useState<ImportItem[]>([]);
    
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const selectedFile = e.target.files[0];
            setFile(selectedFile);
            setError(null);
            
            try {
                const parsed = await parseGpxFile(selectedFile);
                
                const newItems: ImportItem[] = parsed.map(f => {
                    const isWaypoint = f.type === 'Waypoint';
                    
                    // Initial Feature State
                    const initialFeature: Partial<MapFeature> = {
                        map_feature_id: '', // Empty ID for new
                        label: f.name,
                        farm_id: farm.farm_id,
                        feature_type: isWaypoint ? MapFeatureType.PointOfInterest : MapFeatureType.Field, // Default
                        color: isWaypoint ? '#9333ea' : '#3388ff',
                        fill_opacity: isWaypoint ? 0 : 0.2,
                        stroke_weight: isWaypoint ? 6 : 3,
                        geometry_type: isWaypoint ? GeometryType.Point : GeometryType.Polygon
                    };

                    return {
                        id: f.id,
                        selected: true,
                        feature: initialFeature,
                        rawGeometry: f.coordinates,
                        rawType: f.type
                    };
                });

                setItems(newItems);
                setStep(2);
            } catch (err) {
                setError("Failed to parse GPX file. Ensure it is valid XML.");
                console.error(err);
            }
        }
    };

    const handleItemUpdate = (id: string, updates: Partial<MapFeature>) => {
        setItems(prev => prev.map(item => {
            if (item.id === id) {
                return { ...item, feature: { ...item.feature, ...updates } };
            }
            return item;
        }));
    };

    const toggleSelection = (id: string) => {
        setItems(prev => prev.map(item => item.id === id ? { ...item, selected: !item.selected } : item));
    };

    const handleSubmit = async () => {
        if (!file) return;
        setIsProcessing(true);
        try {
            // 1. Save Document
            const newDoc: Document = {
                document_id: '',
                title: file.name,
                file_path: `/uploads/${file.name}`,
                file_type: 'gpx',
                source: DocumentSource.Uploaded,
                uploaded_date: new Date().toISOString(),
                is_confidential: false,
                notes: `Imported via Map Explorer`,
                server_version: 0
            };
            const savedDoc = await MockAPI.saveDocument(newDoc);

            // 2. Process Items
            for (const item of items) {
                if (!item.selected) continue;

                const { feature, rawGeometry, rawType } = item;
                const cat = getCategoryFromFeature(feature);

                // Final Geometry Determination
                let geoType = feature.geometry_type || GeometryType.Polyline;
                
                // If it's Land/Structure, force Polygon logic if coordinates allow
                if ((cat === 'Land' || cat === 'Structure') && rawType !== 'Waypoint') {
                    geoType = determineGeometryType(rawGeometry as [number, number][]);
                    // Force polygon if user selected Land, even if open line (will auto-close)
                    if (cat === 'Land') geoType = GeometryType.Polygon;
                } else if (cat === 'Trail') {
                    geoType = GeometryType.Polyline;
                } else if (cat === 'POI') {
                    geoType = GeometryType.Point;
                }

                // Entity Linking: Update Entity Geometry AND Save Annotation
                if (cat === 'Land' && feature.entity_type && feature.entity_id) {
                    if (feature.entity_type === 'farm') {
                        const f = await MockAPI.getFarm(feature.entity_id);
                        if (f) { f.boundary_geojson = JSON.stringify(rawGeometry); await MockAPI.saveFarm(f); }
                    }
                }

                // ALWAYS Save as explicit MapFeature annotation to persist specific style settings
                // (This ensures opacity and custom colors are not lost when linking to entity)
                const mapFeature: MapFeature = {
                    map_feature_id: '', // New ID
                    farm_id: farm.farm_id,
                    feature_type: feature.feature_type,
                    entity_type: feature.entity_type,
                    entity_id: feature.entity_id,
                    geometry_type: geoType,
                    coordinates_geojson: JSON.stringify(rawGeometry),
                    label: feature.label,
                    color: feature.color,
                    fill_color: feature.color, // Sync for safety
                    stroke_weight: feature.stroke_weight,
                    fill_opacity: feature.fill_opacity,
                    source: 'imported_gpx',
                    gpx_source_document_id: savedDoc.document_id,
                    gpx_source_segment_name: feature.label,
                    notes: `Imported from ${file.name}`,
                    server_version: 0
                };
                if (mapFeature.entity_type && !SUPPORTED_MAP_ENTITY_TYPES.has(mapFeature.entity_type)) {
                    mapFeature.entity_type = undefined;
                    mapFeature.entity_id = undefined;
                }
                await MockAPI.saveMapFeature(mapFeature);
            }

            void SyncService.requestSync('mutation');
            onSuccess();
        } catch (err) {
            console.error(err);
            setError("Failed to save places.");
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="flex flex-col h-full max-h-[90vh]">
            <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-xl font-bold text-gray-900">Import Places</h2>
                <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><X className="h-5 w-5"/></button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
                {step === 1 && (
                    <div className="flex flex-col items-center justify-center h-64 border-2 border-gray-300 border-dashed rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                        <label htmlFor="gpx-modal-upload" className="cursor-pointer flex flex-col items-center">
                            <Upload className="h-10 w-10 text-gray-400 mb-3" />
                            <span className="text-sm font-medium text-gray-700">Click to upload GPX file</span>
                            <span className="text-xs text-gray-500 mt-1">Tracks, Routes, and Waypoints supported</span>
                            <input id="gpx-modal-upload" type="file" onChange={handleFileChange} accept=".gpx" className="hidden" />
                        </label>
                        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
                    </div>
                )}

                {step === 2 && (
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-gray-600">Found {items.length} feature(s) in <strong>{file?.name}</strong>.</p>
                            <Button variant="secondary" size="sm" onClick={() => setStep(1)}>Choose different file</Button>
                        </div>

                        <div className="space-y-4">
                            {items.map(item => (
                                <div key={item.id} className={`border rounded-lg p-4 transition-colors ${item.selected ? 'bg-white border-gray-300 shadow-sm' : 'bg-gray-50 border-gray-200 opacity-60'}`}>
                                    <div className="flex items-start gap-4">
                                        <div className="pt-2">
                                            <input 
                                                type="checkbox" 
                                                checked={item.selected} 
                                                onChange={() => toggleSelection(item.id)}
                                                className="h-4 w-4 text-usda-600 rounded border-gray-300 focus:ring-usda-500"
                                            />
                                        </div>
                                        
                                        <div className="flex-1">
                                            <div className="flex justify-between items-center mb-2">
                                                <h4 className="font-medium text-gray-900">{item.feature.label}</h4>
                                                <span className="text-xs text-gray-500 uppercase tracking-wide">{item.rawType}</span>
                                            </div>
                                            
                                            {item.selected && (
                                                <div className="pt-2 border-t border-gray-100">
                                                    <MapFeatureEditor 
                                                        feature={item.feature}
                                                        onChange={(updates) => handleItemUpdate(item.id, updates)}
                                                        farmId={farm.farm_id}
                                                        tracts={[]}
                                                        fields={[]}
                                                        showLabel={false}
                                                        variant="compact"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className="p-4 border-t bg-gray-50 flex justify-end space-x-3">
                <Button variant="outline" onClick={onClose}>Cancel</Button>
                {step === 2 && (
                    <Button onClick={handleSubmit} disabled={isProcessing}>
                        {isProcessing ? 'Importing...' : 'Save Features'}
                    </Button>
                )}
            </div>
        </div>
    );
};

export default MapImportModal;
